import type { IQuoteBreakpoints } from '@/common/interfaces/quoter.interface';
import {
  buildStoreCartPriceChangeWarningMessage,
  STORE_CART_PRICE_DECREASED_WORD,
  STORE_CART_PRICE_INCREASED_WORD,
} from '@/modules/Cart/StoreCart/storeCart.constants';

/** Store cart minimum quantity tiers shown in the UI (default brands) */
export const STORE_CART_MIN_QUANTITY_TIERS = [12, 24, 30, 48, 72, 144] as const;

/** Fresh Prints apparel uses a 50-piece minimum and higher breakpoints only */
export const STORE_CART_FRESH_PRINTS_MIN_QUANTITY_TIERS = [50, 72, 144] as const;

/** Store cart drawer minimum tiers — same for all brands (no Fresh Prints floor) */
export const STORE_CART_DRAWER_MIN_QUANTITY_TIERS = [12, 24, 36, 48, 72, 144, 288] as const;

const MOCK_TIER_MULTIPLIERS: Record<number, number> = {
  12: 1,
  24: 0.95,
  30: 0.92,
  48: 0.88,
  72: 0.85,
  144: 0.8,
};

const MOCK_FRESH_PRINTS_TIER_MULTIPLIERS: Record<number, number> = {
  50: 1,
  72: 0.85,
  144: 0.8,
};

const DRAWER_MOCK_TIER_MULTIPLIERS: Record<number, number> = {
  12: 1,
  24: 0.95,
  36: 0.92,
  48: 0.88,
  72: 0.85,
  144: 0.8,
  288: 0.75,
};

const normalizeQuantity = (quantity: number | string): number => Number(quantity);

export const isFreshPrintsApparelBrand = (brand: string | undefined | null): boolean =>
  Boolean(brand?.toUpperCase().includes('FRESH PRINTS'));

const getUnfilteredBreakpointQuantities = (
  priceList: IQuoteBreakpoints[] | undefined
): number[] => {
  if (!priceList?.length) {
    return [];
  }

  return [
    ...new Set(
      priceList
        .map((breakpoint) => normalizeQuantity(breakpoint.quantity))
        .filter((quantity) => quantity > 0)
    ),
  ].sort((a, b) => a - b);
};

/** Distinct positive breakpoint quantities from a quoter price list (no UI fallback tiers). */
export const getDistinctBreakpointQuantitiesFromPriceList = (
  priceList: IQuoteBreakpoints[] | undefined,
  brand?: string
): number[] => {
  const quantities = getUnfilteredBreakpointQuantities(priceList);

  if (isFreshPrintsApparelBrand(brand)) {
    return quantities.filter(
      (quantity) => quantity >= STORE_CART_FRESH_PRINTS_MIN_QUANTITY_TIERS[0]
    );
  }

  return quantities;
};

const getMockTierMultipliers = (brand: string | undefined): Record<number, number> =>
  isFreshPrintsApparelBrand(brand) ? MOCK_FRESH_PRINTS_TIER_MULTIPLIERS : MOCK_TIER_MULTIPLIERS;

const getApplicableBreakpointFromPriceList = (
  quantity: number,
  priceList: IQuoteBreakpoints[]
): IQuoteBreakpoints | null => {
  const sortedBreakpoints = [...priceList].sort(
    (a, b) => normalizeQuantity(a.quantity) - normalizeQuantity(b.quantity)
  );

  let applicableBreakpoint: IQuoteBreakpoints | null = null;

  for (let index = sortedBreakpoints.length - 1; index >= 0; index -= 1) {
    const breakpoint = sortedBreakpoints[index];

    if (
      breakpoint &&
      normalizeQuantity(breakpoint.quantity) &&
      quantity >= normalizeQuantity(breakpoint.quantity)
    ) {
      applicableBreakpoint = breakpoint;
      break;
    }
  }

  if (!applicableBreakpoint && sortedBreakpoints.length > 0) {
    const [lowestQuantityBreakpoint] = sortedBreakpoints;

    if (quantity < normalizeQuantity(lowestQuantityBreakpoint.quantity)) {
      return null;
    }

    return lowestQuantityBreakpoint;
  }

  return applicableBreakpoint;
};

export const getStoreCartMinQuantityTiers = (
  brand: string | undefined,
  priceList?: IQuoteBreakpoints[]
): readonly number[] => {
  if (isFreshPrintsApparelBrand(brand)) {
    if (priceList?.length) {
      const freshPrintsTiersFromPriceList = [
        ...new Set(
          priceList
            .map((breakpoint) => normalizeQuantity(breakpoint.quantity))
            .filter((quantity) => quantity >= STORE_CART_FRESH_PRINTS_MIN_QUANTITY_TIERS[0])
        ),
      ].sort((a, b) => a - b);

      if (freshPrintsTiersFromPriceList.length >= 2) {
        return freshPrintsTiersFromPriceList;
      }
    }

    return STORE_CART_FRESH_PRINTS_MIN_QUANTITY_TIERS;
  }

  if (priceList?.length) {
    const tiersFromPriceList = getUnfilteredBreakpointQuantities(priceList);

    if (tiersFromPriceList.length >= 2) {
      return tiersFromPriceList;
    }
  }

  return STORE_CART_MIN_QUANTITY_TIERS;
};

/** Store cart drawer Select Minimum pills — unified tiers for every brand */
export const getStoreCartDrawerMinQuantityTiers = (
  priceList?: IQuoteBreakpoints[]
): readonly number[] => {
  const tiersFromPriceList = priceList?.length ? getUnfilteredBreakpointQuantities(priceList) : [];

  if (tiersFromPriceList.length >= 2) {
    return [...new Set([...STORE_CART_DRAWER_MIN_QUANTITY_TIERS, ...tiersFromPriceList])].sort(
      (a, b) => a - b
    );
  }

  return STORE_CART_DRAWER_MIN_QUANTITY_TIERS;
};

const getApplicableMockTierQuantity = (quantity: number, brand: string | undefined): number => {
  const tiers = [...getStoreCartMinQuantityTiers(brand)].sort((a, b) => b - a);
  const matchedTier = tiers.find((tier) => quantity >= tier);

  return matchedTier ?? tiers[tiers.length - 1];
};

const getApplicableDrawerMockTierQuantity = (quantity: number): number => {
  const tiers = [...STORE_CART_DRAWER_MIN_QUANTITY_TIERS].sort((a, b) => b - a);
  const matchedTier = tiers.find((tier) => quantity >= tier);

  return matchedTier ?? tiers[tiers.length - 1];
};

export const getStoreCartDefaultMinQuantity = (
  brand: string | undefined,
  priceList?: IQuoteBreakpoints[]
): number => getStoreCartMinQuantityTiers(brand, priceList)[0];

export const getStoreCartDrawerDefaultMinQuantity = (priceList?: IQuoteBreakpoints[]): number =>
  getStoreCartDrawerMinQuantityTiers(priceList)[0];

/**
 * When cart quantity is still unset (initial add) and MOQ hydration reveals a lower
 * first breakpoint, reset to that floor unless the user already saved a minimum.
 */
export const shouldResetStoreCartDesignGroupToDefaultMinimum = (
  currentMin: number,
  defaultMin: number,
  savedMinimumQuantity?: number
): boolean => {
  if (Number(savedMinimumQuantity ?? 0) > 0) {
    return false;
  }

  if (!(defaultMin > 0)) {
    return false;
  }

  if (currentMin <= 0) {
    return true;
  }

  const drawerDefault = STORE_CART_DRAWER_MIN_QUANTITY_TIERS[0];

  return currentMin === drawerDefault && defaultMin < currentMin;
};

/**
 * MOQ floor for drawer tiers: prefer API design-group MOQ, else a persisted sub-default
 * minimum (e.g. 1) saved on cart lines before MOQ hydration completes.
 */
export const resolveStoreCartDesignGroupMoqFloor = (
  designGroupMoq: number | undefined,
  referenceMinQuant?: number
): number | undefined => {
  if (designGroupMoq != null && designGroupMoq > 0) {
    return designGroupMoq;
  }

  const savedMinQuant = Number(referenceMinQuant);
  const drawerDefault = STORE_CART_DRAWER_MIN_QUANTITY_TIERS[0];

  if (Number.isFinite(savedMinQuant) && savedMinQuant > 0 && savedMinQuant < drawerDefault) {
    return savedMinQuant;
  }

  return undefined;
};

/** Skip a default breakpoint when it is exactly this many units above design-group maxMoq */
const MAX_MOQ_NEAR_SKIP_DIFF = 5;

/**
 * Build drawer Select Minimum tiers when a design-group maxMoq is present:
 * maxMoq is first, then default breakpoints above it, skipping any within 5 units (diff <= 5).
 */
export const applyMoqFloorToMinQuantityTiers = (
  tiers: readonly number[],
  maxMoq?: number
): number[] => {
  if (maxMoq == null || maxMoq <= 0) {
    return [...tiers];
  }

  const additionalTiers = STORE_CART_DRAWER_MIN_QUANTITY_TIERS.filter(
    (tier) => tier > maxMoq && tier - maxMoq > MAX_MOQ_NEAR_SKIP_DIFF
  );

  return [maxMoq, ...additionalTiers];
};

/** Frozen reference unit price — never derived from the currently selected tier price */
export const getStoreCartBaseUnitPrice = (
  priceList: IQuoteBreakpoints[] | undefined,
  apparelPrice: number,
  brand?: string
): number => {
  if (priceList?.length) {
    const defaultTierQty = getStoreCartDefaultMinQuantity(brand, priceList);
    const defaultTier = priceList.find(
      (breakpoint) => normalizeQuantity(breakpoint.quantity) === defaultTierQty
    );

    if (defaultTier?.finalPricePerUnit != null) {
      return defaultTier.finalPricePerUnit;
    }

    const applicableBreakpoint = getApplicableBreakpointFromPriceList(defaultTierQty, priceList);

    if (applicableBreakpoint?.finalPricePerUnit != null) {
      return applicableBreakpoint.finalPricePerUnit;
    }
  }

  return apparelPrice;
};

export const getStoreCartDrawerBaseUnitPrice = (
  priceList: IQuoteBreakpoints[] | undefined,
  apparelPrice: number
): number => {
  if (priceList?.length) {
    const defaultTierQty = getStoreCartDrawerDefaultMinQuantity(priceList);
    const defaultTier = priceList.find(
      (breakpoint) => normalizeQuantity(breakpoint.quantity) === defaultTierQty
    );

    if (defaultTier?.finalPricePerUnit != null) {
      return defaultTier.finalPricePerUnit;
    }

    const applicableBreakpoint = getApplicableBreakpointFromPriceList(defaultTierQty, priceList);

    if (applicableBreakpoint?.finalPricePerUnit != null) {
      return applicableBreakpoint.finalPricePerUnit;
    }
  }

  return apparelPrice;
};

export const resolveStoreCartFrozenBaseUnitPrice = (
  baseUnitPrice: number | undefined,
  apparelPrice: number,
  priceList: IQuoteBreakpoints[] | undefined,
  brand?: string
): number => {
  if (typeof baseUnitPrice === 'number' && baseUnitPrice > 0) {
    return baseUnitPrice;
  }

  return getStoreCartBaseUnitPrice(priceList, apparelPrice, brand);
};

export const resolveStoreCartDrawerFrozenBaseUnitPrice = (
  baseUnitPrice: number | undefined,
  apparelPrice: number,
  priceList: IQuoteBreakpoints[] | undefined
): number => {
  if (typeof baseUnitPrice === 'number' && baseUnitPrice > 0) {
    return baseUnitPrice;
  }

  return getStoreCartDrawerBaseUnitPrice(priceList, apparelPrice);
};

/**
 * Price for a selected minimum tier — matches store checkout breakpoint logic.
 * Uses quoter priceList when available; mock tiers always multiply a frozen base only.
 */
export const resolveStoreCartPreviousUnitPrice = (
  strikedPricePerUnit: number | undefined | null,
  frozenBaseUnitPrice: number,
  itemCurrentPrice: number,
  apparelPrice?: number,
  savedCartPrice?: number
): number | null => {
  const candidatePrices = [strikedPricePerUnit, savedCartPrice, apparelPrice, frozenBaseUnitPrice];

  const matchedPrice = candidatePrices.find(
    (price): price is number => typeof price === 'number' && price > 0 && price !== itemCurrentPrice
  );

  return matchedPrice ?? null;
};

/** Quoter-sourced unit price only — never uses mock tier multipliers */
export const getStoreCartPriceFromPriceListOnly = (
  quantity: number,
  priceList: IQuoteBreakpoints[] | undefined,
  options?: { exactQuantity?: boolean }
): number | null => {
  const qty = normalizeQuantity(quantity);

  if (!priceList?.length) {
    return null;
  }

  if (options?.exactQuantity) {
    const exactBreakpoint = priceList.find(
      (breakpoint) => normalizeQuantity(breakpoint.quantity) === qty
    );

    return exactBreakpoint?.finalPricePerUnit != null ? exactBreakpoint.finalPricePerUnit : null;
  }

  const applicableBreakpoint = getApplicableBreakpointFromPriceList(qty, priceList);

  if (applicableBreakpoint?.finalPricePerUnit != null) {
    return applicableBreakpoint.finalPricePerUnit;
  }

  return null;
};

/**
 * Unit price for the selected minimum — prefers an exact quoter breakpoint for that
 * quantity. Never inherits a lower-tier applicable price for MOQ floors like 16.
 */
export const getStoreCartQuotedItemPrice = (
  quantity: number,
  item: {
    price?: number;
    priceList?: IQuoteBreakpoints[];
    minQuant?: number;
    quantity?: number;
  }
): number | null => {
  const normalizedQuantity = Number(quantity);
  const itemMinimum = Number(item.minQuant ?? item.quantity ?? 0);
  const exactListPrice = getStoreCartPriceFromPriceListOnly(normalizedQuantity, item.priceList, {
    exactQuantity: true,
  });

  if (exactListPrice != null && exactListPrice > 0) {
    return exactListPrice;
  }

  // No exact breakpoint for this minimum. Only trust the form/API unit price when
  // there is no price list yet — otherwise a stale lower-tier price can linger
  // after minQuant was updated (e.g. MOQ floor 16 still showing the qty-12 price).
  if (
    typeof item.price === 'number' &&
    item.price > 0 &&
    itemMinimum === normalizedQuantity &&
    !item.priceList?.length
  ) {
    return item.price;
  }

  return null;
};

export const getStoreCartPriceChangeWarningMessage = (
  currentPrice: number,
  previousPrice: number
): string | null => {
  if (currentPrice === previousPrice) {
    return null;
  }

  return buildStoreCartPriceChangeWarningMessage(
    currentPrice > previousPrice ? STORE_CART_PRICE_INCREASED_WORD : STORE_CART_PRICE_DECREASED_WORD
  );
};

export const getStoreCartPriceForQuantity = (
  quantity: number,
  priceList: IQuoteBreakpoints[] | undefined,
  frozenBaseUnitPrice: number,
  brand?: string
): number => {
  const qty = normalizeQuantity(quantity);

  if (priceList?.length) {
    const applicableBreakpoint = getApplicableBreakpointFromPriceList(qty, priceList);

    if (applicableBreakpoint?.finalPricePerUnit != null) {
      return applicableBreakpoint.finalPricePerUnit;
    }
  }

  const applicableTierQty = getApplicableMockTierQuantity(qty, brand);
  const multiplier = getMockTierMultipliers(brand)[applicableTierQty];

  if (multiplier != null) {
    return frozenBaseUnitPrice * multiplier;
  }

  return frozenBaseUnitPrice;
};

/** Store cart drawer tier pricing — unified tiers for every brand */
export const getStoreCartDrawerPriceForQuantity = (
  quantity: number,
  priceList: IQuoteBreakpoints[] | undefined,
  frozenBaseUnitPrice: number
): number => {
  const qty = normalizeQuantity(quantity);

  if (priceList?.length) {
    const applicableBreakpoint = getApplicableBreakpointFromPriceList(qty, priceList);

    if (applicableBreakpoint?.finalPricePerUnit != null) {
      return applicableBreakpoint.finalPricePerUnit;
    }
  }

  const applicableTierQty = getApplicableDrawerMockTierQuantity(qty);
  const multiplier = DRAWER_MOCK_TIER_MULTIPLIERS[applicableTierQty];

  if (multiplier != null) {
    return frozenBaseUnitPrice * multiplier;
  }

  return frozenBaseUnitPrice;
};
